import axios from 'axios';
import type {
  AddPravaraPayload,
  CreateGotraPayload,
  CreatePravaraGroupPayload,
  CreateGotraSuggestionPayload,
  Gotra,
  GotraSeedResult,
  GotraSuggestion,
  UpdateGotraPayload,
  UpdatePravaraGroupPayload,
  UpdatePravaraPayload,
  UpdateSurnamePayload,
} from '../types/gotra';
import { AUTH_URL } from '../config/apiUrls';
import { attachAuthInterceptors } from './attachAuthInterceptors';

export { AUTH_URL };

const publicGotraApi = axios.create({
  baseURL: AUTH_URL,
});

const authenticatedGotraApi = axios.create({
  baseURL: AUTH_URL,
});

attachAuthInterceptors(authenticatedGotraApi);

export const gotraApi = {
  getAll: () => publicGotraApi.get<Gotra[]>('/gotras'),
  submitSuggestion: (payload: CreateGotraSuggestionPayload) =>
    publicGotraApi.post<GotraSuggestion>('/gotras/suggestions', payload),

  listSuggestions: () =>
    authenticatedGotraApi.get<GotraSuggestion[]>('/gotras/suggestions'),
  getById: (id: number) => authenticatedGotraApi.get<Gotra>(`/gotras/${id}`),
  create: (payload: CreateGotraPayload) =>
    authenticatedGotraApi.post<Gotra>('/gotras', payload),
  update: (id: number, payload: UpdateGotraPayload) =>
    authenticatedGotraApi.put<Gotra>(`/gotras/${id}`, payload),
  delete: (id: number) => authenticatedGotraApi.delete(`/gotras/${id}`),
  validate: (id: number) => authenticatedGotraApi.post<Gotra>(`/gotras/${id}/validate`),
  unvalidate: (id: number) => authenticatedGotraApi.post<Gotra>(`/gotras/${id}/unvalidate`),
  seed: () => authenticatedGotraApi.post<GotraSeedResult>('/gotras/seed'),

  validatePravara: (gotraId: number, pravaraId: number) =>
    authenticatedGotraApi.post<Gotra>(`/gotras/${gotraId}/pravaras/${pravaraId}/validate`),
  unvalidatePravara: (gotraId: number, pravaraId: number) =>
    authenticatedGotraApi.post<Gotra>(`/gotras/${gotraId}/pravaras/${pravaraId}/unvalidate`),
  validatePravaraGroup: (gotraId: number, groupId: number) =>
    authenticatedGotraApi.post<Gotra>(`/gotras/${gotraId}/pravara-groups/${groupId}/validate`),
  unvalidatePravaraGroup: (gotraId: number, groupId: number) =>
    authenticatedGotraApi.post<Gotra>(`/gotras/${gotraId}/pravara-groups/${groupId}/unvalidate`),
  validateSurname: (gotraId: number, surnameId: number) =>
    authenticatedGotraApi.post<Gotra>(`/gotras/${gotraId}/surnames/${surnameId}/validate`),
  unvalidateSurname: (gotraId: number, surnameId: number) =>
    authenticatedGotraApi.post<Gotra>(`/gotras/${gotraId}/surnames/${surnameId}/unvalidate`),
  validateCategory: (gotraId: number, surnameId: number, categoryId: number) =>
    authenticatedGotraApi.post<Gotra>(
      `/gotras/${gotraId}/surnames/${surnameId}/categories/${categoryId}/validate`,
    ),
  unvalidateCategory: (gotraId: number, surnameId: number, categoryId: number) =>
    authenticatedGotraApi.post<Gotra>(
      `/gotras/${gotraId}/surnames/${surnameId}/categories/${categoryId}/unvalidate`,
    ),

  addPravara: (gotraId: number, payload: AddPravaraPayload) =>
    authenticatedGotraApi.post(`/gotras/${gotraId}/pravaras`, payload),
  updatePravara: (gotraId: number, pravaraId: number, payload: UpdatePravaraPayload) =>
    authenticatedGotraApi.put(`/gotras/${gotraId}/pravaras/${pravaraId}`, payload),
  unlinkPravara: (gotraId: number, pravaraId: number) =>
    authenticatedGotraApi.delete(`/gotras/${gotraId}/pravaras/${pravaraId}`),

  createPravaraGroup: (gotraId: number, payload: CreatePravaraGroupPayload) =>
    authenticatedGotraApi.post<Gotra>(`/gotras/${gotraId}/pravara-groups`, payload),
  updatePravaraGroup: (
    gotraId: number,
    groupId: number,
    payload: UpdatePravaraGroupPayload,
  ) => authenticatedGotraApi.put(`/gotras/${gotraId}/pravara-groups/${groupId}`, payload),
  deletePravaraGroup: (gotraId: number, groupId: number) =>
    authenticatedGotraApi.delete(`/gotras/${gotraId}/pravara-groups/${groupId}`),

  addSurname: (
    gotraId: number,
    payload: { name: string; description?: string | null; categories?: Array<{ name: string }> },
  ) => authenticatedGotraApi.post(`/gotras/${gotraId}/surnames`, payload),
  updateSurname: (gotraId: number, surnameId: number, payload: UpdateSurnamePayload) =>
    authenticatedGotraApi.put(`/gotras/${gotraId}/surnames/${surnameId}`, payload),
  deleteSurname: (gotraId: number, surnameId: number) =>
    authenticatedGotraApi.delete(`/gotras/${gotraId}/surnames/${surnameId}`),

  addCategory: (gotraId: number, surnameId: number, name: string) =>
    authenticatedGotraApi.post(`/gotras/${gotraId}/surnames/${surnameId}/categories`, {
      name,
    }),
  updateCategory: (
    gotraId: number,
    surnameId: number,
    categoryId: number,
    name: string,
  ) =>
    authenticatedGotraApi.put(
      `/gotras/${gotraId}/surnames/${surnameId}/categories/${categoryId}`,
      { name },
    ),
  deleteCategory: (gotraId: number, surnameId: number, categoryId: number) =>
    authenticatedGotraApi.delete(
      `/gotras/${gotraId}/surnames/${surnameId}/categories/${categoryId}`,
    ),
};
